import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ChatMessages from "../Chat/ChatMessages";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import RichTextRenderer from "../../molecules/Composer/RichTextRenderer";
import ChatComposer from "../../molecules/ChatComposer";
import useUserDirectoryResolver from "../../../hooks/useUserDirectoryResolver";

export default function BandChatPanel({
    groupDetails,
    channel,
    messages,
    setMessages,
    messagesRef,
    isLoading,
    hasMoreAbove = false,
    isFetchingOlder = false,
    onLoadOlderMessages,
    currentUserId,
    messageDraft,
    onMessageDraftChange,
    onSendMessage,
    onDeleteMessage,
    onEditMessage,
    onOpenChannel,
    userOptions = [],
    onOpenUser,
    channelOptions = [],
    isSending = false,
    onShowFiles,
    onReactMessage,
    commentPanelState,
    onCommentPanelStateChange,
    conversationKey,
}) {
    const { t } = useTranslation();
    const groupId = groupDetails?.id;
    const members = useMemo(() => (Array.isArray(groupDetails?.members) ? groupDetails.members : []), [groupDetails?.members]);
    const resolveChannelUser = useUserDirectoryResolver({
        currentUserId,
        members,
        messages,
        userOptions,
    });

    if (!channel) {
        return (
            <div className="workspace-placeholder">
                <p>{t("bandsPage.chat.empty")}</p>
            </div>
        );
    }

    const groupLabel = groupDetails?.name || t("bandsPage.channelCreator.header.workspaceFallback");

    return (
        <div className="workspace-surface">
            {isLoading ? (
                <div className="workspace-placeholder">
                    <LoadingSpinner size={40} />
                </div>
            ) : (
                <ChatMessages
                    ref={messagesRef}
                    messages={messages}
                    setMessages={setMessages}
                    currentUserId={currentUserId}
                    typingUserIds={[]}
                    resolveUserName={resolveChannelUser}
                    onDeleteMessage={onDeleteMessage}
                    onEditMessage={onEditMessage}
                    onReact={onReactMessage}
                    onComment={(msg) => { /* open comment drawer */ }}
                    onMore={(msg) => { /* open contextual menu */ }}
                    commentPanelState={commentPanelState}
                    onCommentPanelStateChange={onCommentPanelStateChange}
                    contentRenderer={(html) => (
                        <RichTextRenderer
                            html={html}
                            channels={channelOptions}
                            friends={userOptions}
                            groupId={groupId}
                            onOpenChannel={onOpenChannel}
                            onOpenUser={onOpenUser}
                        />
                    )}
                    hasMoreAbove={hasMoreAbove}
                    isFetchingOlder={isFetchingOlder}
                    onLoadOlderMessages={onLoadOlderMessages}
                    conversationKey={conversationKey}
                />
            )}
            <ChatComposer
                valueHtml={messageDraft}
                onChangeHtml={onMessageDraftChange}
                isSending={isSending}
                onSend={onSendMessage}
                mentionUsers={userOptions}
                mentionChannels={channelOptions}
                onInsertCanvas={() => onShowFiles?.()}
                ariaLabel={t("bandsPage.chat.composerAria")}
                placeholderTarget={groupLabel}
            />
        </div>
    );
}

BandChatPanel.propTypes = {
    groupDetails: PropTypes.object,
    channel: PropTypes.shape({
        id: PropTypes.string,
        slug: PropTypes.string,
        description: PropTypes.string,
        name: PropTypes.string,
    }),
    messages: PropTypes.arrayOf(PropTypes.object),
    setMessages: PropTypes.func,
    messagesRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    isLoading: PropTypes.bool,
    hasMoreAbove: PropTypes.bool,
    isFetchingOlder: PropTypes.bool,
    onLoadOlderMessages: PropTypes.func,
    currentUserId: PropTypes.string,
    messageDraft: PropTypes.string,
    onMessageDraftChange: PropTypes.func.isRequired,
    onSendMessage: PropTypes.func.isRequired,
    onDeleteMessage: PropTypes.func,
    onEditMessage: PropTypes.func,
    onOpenChannel: PropTypes.func,
    onOpenUser: PropTypes.func,
    channelOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        slug: PropTypes.string,
        privateChannel: PropTypes.bool,
        name: PropTypes.string,
        description: PropTypes.string,
    })),
    userOptions: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.string.isRequired,
        username: PropTypes.string,
    })),
    isSending: PropTypes.bool,
    onShowFiles: PropTypes.func,
    onReactMessage: PropTypes.func,
    commentPanelState: PropTypes.shape({
        open: PropTypes.bool,
        messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        message: PropTypes.object,
    }),
    onCommentPanelStateChange: PropTypes.func,
    conversationKey: PropTypes.string,
};

BandChatPanel.defaultProps = {
    groupDetails: null,
    channel: null,
    messages: [],
    setMessages: undefined,
    messagesRef: undefined,
    isLoading: false,
    hasMoreAbove: false,
    isFetchingOlder: false,
    onLoadOlderMessages: undefined,
    currentUserId: null,
    messageDraft: "",
    isSending: false,
    onOpenChannel: undefined,
    onOpenUser: undefined,
    onShowFiles: undefined,
    channelOptions: [],
    userOptions: [],
    onReactMessage: undefined,
    commentPanelState: null,
    onCommentPanelStateChange: undefined,
    conversationKey: undefined,
};
